/**
 * Per-row serialized request queue shared by every editable grid.
 *
 *   enqueue(rowId, req) ──▶ 'started'    dispatched immediately
 *                      ├──▶ 'queued'     row busy, runs FIFO after it
 *                      ├──▶ 'coalesced'  duplicate create/delete or merged coalescable update tail
 *                      └──▶ 'disposed'   queue torn down, rejected
 *
 *   row lifecycle:  enqueue ──▶ in flight ──▶ settle ──▶ subscribeRowIdle
 *                                   ▲             │
 *                                   └── FIFO tail ┘
 *
 * INVARIANTS
 *   · AT MOST one request per rowId is in flight; rows run concurrently.
 *   · AT MOST one `create` per row ever reaches the backend (ghost rows, TAS-3271).
 *   · A `delete` is terminal — once dispatched or succeeded, no further DELETE for
 *     that rowId reaches the backend ("Line item does not exist" storm, #4882).
 *   · A failed request cancels that row's tail; other rows are untouched.
 *   · Consumer callbacks can never strand a row — every one runs via invokeSafely.
 *   · reset()/dispose() bump a generation so captured work's late completion is
 *     ignored instead of racing the replacement queue.
 */

import type { GridRowId } from '@mui/x-data-grid-premium';
import { useCallback, useMemo, useRef } from 'react';

type EventType = 'create' | 'update' | 'delete' | 'other';

/** Per-event payload overrides, declared once per queue instantiation. Most queues drive `{ input }` / `{ id, input }` mutations and take the defaults below; a queue whose mutation takes different variables declares only the arm that differs. */
type PayloadOverrides = Partial<Record<EventType, unknown>>;
type NoPayloadOverrides = Record<never, unknown>;

type PayloadType<I, E extends EventType, P extends PayloadOverrides = NoPayloadOverrides> = E extends keyof P
  ? P[E]
  : E extends 'create'
    ? { input: I }
    : E extends 'update'
      ? { id: number; input: I }
      : E extends 'delete'
        ? number
        : void;

type QueuableRequest<R, I, E extends EventType, P extends PayloadOverrides = NoPayloadOverrides> = E extends EventType
  ? {
      event(payload: PayloadType<I, E, P>): Promise<R>;
      // curried so that we can get the latest id from the state
      unpackPayload: () => PayloadType<I, E, P>;
      eventType: E;
      // Opt-in tail merging. Every field in the payload must be ABSOLUTE-SET so folding two saves into one equals sending them in order; a payload whose fields ACCUMULATE server-side (`addPalletTagToLineItem`) must leave this unset or merging drops a save.
      coalescable?: boolean;
      resolve(result: R): void;
      resolve(result: Partial<R>): void;
      reject: (result: Error) => void;
    }
  : never;

type RuntimeQueuableRequest<R, I, P extends PayloadOverrides = NoPayloadOverrides> = QueuableRequest<
  R,
  I,
  EventType,
  P
>;

/** Resolvers parked by a coalesced create/delete, settled with the leader's outcome. */
type RequestFollower<R, I, P extends PayloadOverrides = NoPayloadOverrides> = Pick<
  RuntimeQueuableRequest<R, I, P>,
  'resolve' | 'reject'
>;

const executeRequest = <R, I, P extends PayloadOverrides = NoPayloadOverrides>(
  request: RuntimeQueuableRequest<R, I, P>
): Promise<R> => {
  // Keeps event and unpackPayload correlated through distributed-union narrowing.
  switch (request.eventType) {
    case 'create':
      return request.event(request.unpackPayload());
    case 'update':
      return request.event(request.unpackPayload());
    case 'delete':
      return request.event(request.unpackPayload());
    case 'other':
      return request.event(request.unpackPayload());
  }
};

export type RequestQueueDisposition = 'started' | 'queued' | 'coalesced' | 'disposed';

export type RequestQueue<R, I, E extends EventType = EventType, P extends PayloadOverrides = NoPayloadOverrides> = {
  enqueue(rowId: GridRowId, queuableRequest: QueuableRequest<R, I, E, P>): RequestQueueDisposition;
}['enqueue'];

export type RequestQueueLifecycle<
  R,
  I,
  E extends EventType = EventType,
  P extends PayloadOverrides = NoPayloadOverrides,
> = RequestQueue<R, I, E, P> & {
  activate: () => void;
  dispose: () => void;
  isRowPending: (rowId: GridRowId) => boolean;
  reset: () => void;
  subscribeRowIdle: (listener: (rowId: GridRowId) => void) => () => void;
};

const normalizeRequestError = (
  error: unknown,
  nonErrorMessage = 'Grid save request failed with a non-Error rejection'
): Error => (error instanceof Error ? error : new Error(nonErrorMessage, { cause: error }));

const invokeSafely = (callback: () => void): void => {
  try {
    callback();
  } catch (error) {
    // Production Faro instrumentation captures console errors. Reporting stays
    // out-of-band so a consumer callback cannot strand later requests.
    console.error(
      'Grid request queue callback failed',
      normalizeRequestError(error, 'Grid request queue callback threw a non-Error value')
    );
  }
};

const isMergeablePayload = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const overlayDefinedKeys = (earlier: Record<string, unknown>, later: Record<string, unknown>) => {
  const merged: Record<string, unknown> = { ...earlier };

  for (const [key, value] of Object.entries(later)) {
    // `undefined` means this save did not touch the field; explicit `null` still clears it.
    if (value !== undefined) {
      merged[key] = value;
    }
  }

  return merged;
};

function mergeUpdatePayloads<I, P extends PayloadOverrides>(
  earlier: PayloadType<I, 'update', P>,
  later: PayloadType<I, 'update', P>
): PayloadType<I, 'update', P>;
function mergeUpdatePayloads(earlier: unknown, later: unknown): unknown {
  if (!isMergeablePayload(earlier) || !isMergeablePayload(later)) {
    return later;
  }

  // Top-level mutation variables are last-write-wins: `undefined` there means "not sent",
  // not "untouched", so backfilling it from the earlier save would resurrect a stale value.
  const merged: Record<string, unknown> = { ...later };

  if (isMergeablePayload(earlier.input) && isMergeablePayload(later.input)) {
    merged.input = overlayDefinedKeys(earlier.input, later.input);
  }

  return merged;
}

const mergeQueuedUpdates = <R, I, P extends PayloadOverrides>(
  earlier: QueuableRequest<R, I, 'update', P>,
  later: QueuableRequest<R, I, 'update', P>
): QueuableRequest<R, I, 'update', P> => ({
  ...later,
  unpackPayload: () => mergeUpdatePayloads(earlier.unpackPayload(), later.unpackPayload()),
  resolve: (result: R) => {
    invokeSafely(() => earlier.resolve(result));
    invokeSafely(() => later.resolve(result));
  },
  reject: (error) => {
    invokeSafely(() => earlier.reject(error));
    invokeSafely(() => later.reject(error));
  },
});

export function useRequestQueue<R, I, E extends EventType = EventType, P extends PayloadOverrides = NoPayloadOverrides>(
  success?: () => void
): RequestQueueLifecycle<R, I, E, P>;
export function useRequestQueue<R, I, E extends EventType = EventType, P extends PayloadOverrides = NoPayloadOverrides>(
  success?: () => void
) {
  const activeRequestsRef = useRef(new Map<GridRowId, RuntimeQueuableRequest<R, I, P>>());
  const disposedRef = useRef(false);
  const generationRef = useRef(0);
  const idleListenersRef = useRef(new Set<(rowId: GridRowId) => void>());
  const unreportedRowsRef = useRef(new Set<GridRowId>());

  // Rows with a `create` already dispatched or queued, so a second `create` coalesces instead of issuing a duplicate INSERT — one draft commit can fan out into two saveChanges calls (MUI fires both `processRowUpdate` and `cellFocusOut`), producing a phantom 0-unit ghost twin. Extra resolvers settle with the original create's result.
  const pendingCreateRef = useRef(new Map<GridRowId, Array<RequestFollower<R, I, P>>>());

  // Mirrors pendingCreateRef for deletes: parks the resolvers of a duplicate delete
  // dispatched while the first delete for the same row is still in flight, then settles
  // them with the original delete's result/error. See deletedRef for the terminal case.
  const pendingDeleteRef = useRef(new Map<GridRowId, Array<RequestFollower<R, I, P>>>());

  // Tombstone of rows whose delete SUCCEEDED. A delete is terminal, so a later delete for the same rowId is swallowed as success rather than re-dispatched — re-dispatch throws `Line item does not exist.` and re-arms the resurrection loop. rowIds are stable db ids, so the tombstone survives reset()/dispose().
  const deletedRef = useRef(new Set<GridRowId>());

  const queuesByIdRef = useRef(new Map<GridRowId, Array<RuntimeQueuableRequest<R, I, P>>>());

  const notifyRowIdle = useCallback((rowId: GridRowId) => {
    idleListenersRef.current.forEach((listener) => invokeSafely(() => listener(rowId)));
  }, []);

  const isRowPending = useCallback(
    (rowId: GridRowId) =>
      activeRequestsRef.current.has(rowId) ||
      Boolean(queuesByIdRef.current.get(rowId)?.length) ||
      pendingCreateRef.current.has(rowId) ||
      pendingDeleteRef.current.has(rowId),
    []
  );

  const notifyRowIdleIfSettled = useCallback(
    (rowId: GridRowId) => {
      if (isRowPending(rowId) || !unreportedRowsRef.current.delete(rowId)) {
        return;
      }
      notifyRowIdle(rowId);
    },
    [isRowPending, notifyRowIdle]
  );

  const settleFollowers = useCallback(
    (
      followers: Map<GridRowId, Array<RequestFollower<R, I, P>>>,
      rowId: GridRowId,
      settle: (extra: RequestFollower<R, I, P>) => void
    ) => {
      const extras = followers.get(rowId);
      followers.delete(rowId);
      extras?.forEach((extra) => invokeSafely(() => settle(extra)));
    },
    []
  );

  const failRow = useCallback(
    (rowId: GridRowId, request: RuntimeQueuableRequest<R, I, P>, error: unknown) => {
      const requestError = normalizeRequestError(error);
      const tail = queuesByIdRef.current.get(rowId) ?? [];
      const createFollowers = pendingCreateRef.current.get(rowId) ?? [];
      // A genuine failure (not "already deleted") leaves the row retryable: the
      // followers are rejected and NO tombstone is recorded.
      const deleteFollowers = pendingDeleteRef.current.get(rowId) ?? [];

      activeRequestsRef.current.delete(rowId);
      queuesByIdRef.current.delete(rowId);
      pendingCreateRef.current.delete(rowId);
      pendingDeleteRef.current.delete(rowId);

      invokeSafely(() => request.reject(requestError));
      tail.forEach((queuedRequest) => invokeSafely(() => queuedRequest.reject(requestError)));
      createFollowers.forEach(({ reject }) => invokeSafely(() => reject(requestError)));
      deleteFollowers.forEach(({ reject }) => invokeSafely(() => reject(requestError)));
      notifyRowIdleIfSettled(rowId);
    },
    [notifyRowIdleIfSettled]
  );

  const processQueue = useCallback(
    (rowId: GridRowId, requestGeneration: number) => {
      const request = queuesByIdRef.current.get(rowId)?.shift();
      if (!request) {
        queuesByIdRef.current.delete(rowId);
        if (requestGeneration === generationRef.current) {
          notifyRowIdle(rowId);
        }
        return;
      }

      activeRequestsRef.current.set(rowId, request);
      unreportedRowsRef.current.add(rowId);
      let requestPromise: Promise<R>;
      try {
        requestPromise = executeRequest(request);
      } catch (error) {
        if (requestGeneration === generationRef.current) {
          failRow(rowId, request, error);
        }
        return;
      }

      requestPromise.then(
        (result) => {
          if (requestGeneration !== generationRef.current) {
            return;
          }

          activeRequestsRef.current.delete(rowId);
          invokeSafely(() => request.resolve(result));
          if (request.eventType === 'create') {
            settleFollowers(pendingCreateRef.current, rowId, ({ resolve }) => resolve(result));
          }

          if (request.eventType === 'delete') {
            // Row is gone on the server — tombstone it and settle any coalesced deletes.
            deletedRef.current.add(rowId);
            settleFollowers(pendingDeleteRef.current, rowId, ({ resolve }) => resolve(result));
          }

          if (success) {
            invokeSafely(success);
          }

          if ((queuesByIdRef.current.get(rowId)?.length ?? 0) === 0) {
            queuesByIdRef.current.delete(rowId);
            notifyRowIdleIfSettled(rowId);
            return;
          }

          processQueue(rowId, requestGeneration);
        },
        (error: unknown) => {
          if (requestGeneration === generationRef.current) {
            failRow(rowId, request, error);
          }
        }
      );
    },
    [failRow, notifyRowIdle, notifyRowIdleIfSettled, settleFollowers, success]
  );

  const enqueue = useCallback<RequestQueue<R, I, E, P>>(
    (rowId, queuableRequest) => {
      if (disposedRef.current) {
        invokeSafely(() => queuableRequest.reject(new Error('Grid request queue has been disposed')));
        return 'disposed';
      }

      const queued = queuesByIdRef.current.get(rowId);

      // Idempotency: never issue a second create for a row whose create is already
      // pending. Park the duplicate's resolvers so they settle with the real create.
      if (queuableRequest.eventType === 'create') {
        if (pendingCreateRef.current.has(rowId)) {
          pendingCreateRef.current
            .get(rowId)
            ?.push({ resolve: queuableRequest.resolve, reject: queuableRequest.reject });
          return 'coalesced';
        }
        pendingCreateRef.current.set(rowId, []);
      }

      // A delete is terminal: never re-dispatch one for a tombstoned row or a delete already in flight — the second hit throws `Line item does not exist.`
      if (queuableRequest.eventType === 'delete') {
        if (deletedRef.current.has(rowId)) {
          // The row is already gone, so there is no server payload to relay.
          invokeSafely(() => queuableRequest.resolve({}));
          return 'coalesced';
        }

        if (pendingDeleteRef.current.has(rowId)) {
          pendingDeleteRef.current
            .get(rowId)
            ?.push({ resolve: queuableRequest.resolve, reject: queuableRequest.reject });
          return 'coalesced';
        }
        pendingDeleteRef.current.set(rowId, []);
      }

      if (queuableRequest.eventType === 'update' && queuableRequest.coalescable && queued?.length) {
        const tail = queued[queued.length - 1];

        if (tail.eventType === 'update' && tail.coalescable && tail.event === queuableRequest.event) {
          queued[queued.length - 1] = mergeQueuedUpdates(tail, queuableRequest);
          return 'coalesced';
        }
      }

      if (!activeRequestsRef.current.has(rowId) && (!queued || queued.length === 0)) {
        queuesByIdRef.current.set(rowId, [queuableRequest]);
        processQueue(rowId, generationRef.current);
        return 'started';
      }

      if (queued) {
        queued.push(queuableRequest);
      } else {
        queuesByIdRef.current.set(rowId, [queuableRequest]);
      }
      return 'queued';
    },
    [processQueue]
  );

  const cancelAll = useCallback(
    (error: Error) => {
      const active = [...activeRequestsRef.current.entries()];
      const queued = [...queuesByIdRef.current.entries()];
      const createFollowers = [...pendingCreateRef.current.entries()];
      const deleteFollowers = [...pendingDeleteRef.current.entries()];
      const affectedRows = new Set<GridRowId>([
        ...active.map(([rowId]) => rowId),
        ...queued.map(([rowId]) => rowId),
        ...createFollowers.map(([rowId]) => rowId),
        ...deleteFollowers.map(([rowId]) => rowId),
      ]);

      generationRef.current++;
      activeRequestsRef.current.clear();
      queuesByIdRef.current.clear();
      pendingCreateRef.current.clear();
      pendingDeleteRef.current.clear();

      active.forEach(([, request]) => invokeSafely(() => request.reject(error)));
      queued.forEach(([, requests]) => requests.forEach((request) => invokeSafely(() => request.reject(error))));
      createFollowers.forEach(([, extras]) => extras.forEach(({ reject }) => invokeSafely(() => reject(error))));
      deleteFollowers.forEach(([, extras]) => extras.forEach(({ reject }) => invokeSafely(() => reject(error))));
      affectedRows.forEach(notifyRowIdleIfSettled);
    },
    [notifyRowIdleIfSettled]
  );

  const activate = useCallback(() => {
    disposedRef.current = false;
  }, []);

  const reset = useCallback(() => {
    if (!disposedRef.current) {
      cancelAll(new Error('Grid request queue has been reset'));
    }
  }, [cancelAll]);

  const dispose = useCallback(() => {
    if (disposedRef.current) {
      return;
    }
    disposedRef.current = true;
    cancelAll(new Error('Grid request queue has been disposed'));
  }, [cancelAll]);

  const subscribeRowIdle = useCallback((listener: (rowId: GridRowId) => void) => {
    idleListenersRef.current.add(listener);
    return () => {
      idleListenersRef.current.delete(listener);
    };
  }, []);

  return useMemo(
    () => Object.assign(enqueue, { activate, dispose, isRowPending, reset, subscribeRowIdle }),
    [activate, dispose, enqueue, isRowPending, reset, subscribeRowIdle]
  );
}
